import * as shapeFactory from "./geometry/shape-factory.js";
import forEach from "./util/for-each.js";

// The order is fixed: stored player preferences and replays hold these ids, and
// the server hands colours out in this order (its WORM_COLOR_IDS mirrors it).
var wormColors = {
    blue: "#03A9F4",
    pink: "#E91E63",
    green: "#4CAF50",
    purple: "#9C27B0",
    orange: "#FF9800",
    lime: "#CDDC39",
    indigo: "#3F51B5",
    teal: "#009688",
    black: "#444",
    bluegrey: "#607D8B",
    red: "#F44336",
    cyan: "#00E5FF",
    yellow: "#FFEE58",
    magenta: "#E040FB",
    sky: "#40C4FF",
    coral: "#FF7043",
    mint: "#1DE9B6",
    gold: "#FFD740",
    violet: "#7C4DFF",
    rose: "#FF4081",
    white: "#FAFAFA",
    // Nine more, so the 30 worms of a Royale are all different. Picked to sit
    // between the hues above and to stay light enough to read on the dark board.
    chartreuse: "#8EE000",
    azure: "#2979FF",
    lavender: "#C9A6FF",
    orchid: "#FF8AD8",
    salmon: "#FF8A80",
    tan: "#C49A6C",
    olive: "#9E9D24",
    seafoam: "#A8F0C8",
    plum: "#B06A9E"
};
var wormColorIds = [];
forEach(wormColors, (color, id) => wormColorIds.push(id));

var powerUpDefinitions = {};
powerUpDefinitions["speed"] = {
    name: "speed",
    effectType: "speed",
    effectDuration: 5,
    effectStrength: 3 / 2,
    weightedSpawnChance: 1,
    affects: "others" // red: everyone else goes faster (harder to steer)
};
powerUpDefinitions["slow"] = {
    name: "slow",
    effectType: "speed",
    effectDuration: 5,
    effectStrength: 2 / 3,
    weightedSpawnChance: 1,
    affects: "self" // green: you slow down (more control)
};
powerUpDefinitions["fat"] = {
    name: "fat",
    effectType: "size",
    effectDuration: 5,
    effectStrength: 2,
    weightedSpawnChance: 1,
    affects: "others"
};
powerUpDefinitions["slim"] = {
    name: "slim",
    effectType: "size",
    effectDuration: 5,
    effectStrength: 0.5,
    weightedSpawnChance: 1,
    affects: "self"
};
powerUpDefinitions["quick_turn"] = {
    name: "quick_turn",
    effectType: "turningSpeed",
    effectDuration: 5,
    effectStrength: 3 / 2,
    weightedSpawnChance: 1,
    affects: "self"
};
powerUpDefinitions["slow_turn"] = {
    name: "slow_turn",
    effectType: "turningSpeed",
    effectDuration: 5,
    effectStrength: 2 / 3,
    weightedSpawnChance: 1,
    affects: "others"
};
powerUpDefinitions["switcharoonie"] = {
    name: "switcharoonie",
    effectType: "wormSwitch",
    weightedSpawnChance: 0.75,
    affects: "all"
};
powerUpDefinitions["key_switch"] = {
    name: "key_switch",
    effectType: "turningSpeed",
    effectDuration: 5,
    effectStrength: -1,
    weightedSpawnChance: 1,
    affects: "others"
};
powerUpDefinitions["drunk"] = {
    name: "drunk",
    effectType: "drunk",
    effectDuration: 5,
    effectStrength: 1,
    weightedSpawnChance: 1,
    affects: "others"
};
powerUpDefinitions["clear_all"] = {
    name: "clear",
    effectType: "clear",
    weightedSpawnChance: 0, // removed from the game for now; definition + effect kept. Restore the weight to re-enable.
    affects: "all"
};
powerUpDefinitions["super_jump"] = {
    name: "super_jump",
    effectType: "superJump",
    effectDuration: 5,
    weightedSpawnChance: 1,
    affects: "self"
};
powerUpDefinitions["tron_turn"] = {
    name: "tron_turn",
    effectType: "tronTurn",
    effectDuration: 5,
    weightedSpawnChance: 1,
    affects: "others"
};
powerUpDefinitions["twin"] = {
    name: "twin",
    effectType: "twin",
    effectDuration: 5,
    weightedSpawnChance: 1,
    affects: "self"
};

powerUpDefinitions["wall_hack_self"] = {
    name: "wall_hack",
    effectType: "wallHack",
    effectDuration: 5,
    weightedSpawnChance: 0, // removed from the game for now (only the blue "all" wall hack spawns). Restore to re-enable.
    affects: "self"
};

powerUpDefinitions["wall_hack_all"] = {
    name: "wall_hack",
    effectType: "wallHack",
    effectDuration: 5,
    weightedSpawnChance: 0.5,
    affects: "all"
};

var START_PHASE_DURATION = 3;
var START_PHASE_DURATION_FIRST = 3; // the first round used to get a longer countdown; the roll call now gives everyone time to get ready
var START_DISTANCE_TO_MAP = 50;
var START_DISTANCE_TO_WORMS = 70;

var POWER_UP_SPAWN_CHANCE = 0.12; //0.12;            // Inverse of maximum time between power up spawns (seconds). 0.1 means max 10 seconds; average 5 seconds.
var POWER_UP_SHAPE = shapeFactory.createCircle(25);
// A red (affects-others) power-up's debuff travels to each victim as a MISSILE at this
// constant speed (game-px/s; worms move at ~WORM_SPEED). The debuff activates on a victim
// when its missile lands: grabTime + distance / RED_MISSILE_SPEED (see activatePowerUp).
// The client's missile animation flies at the same speed, so the visual landing and the
// gameplay activation coincide. Double the distance = double the flight time.
var RED_MISSILE_SPEED = 320;
// Switcharoonie swap-animation length (seconds of game-time): once the host has confirmed
// every player is frozen it schedules goTime = now + lead + SWITCH_ANIM_DURATION, and the
// portal animation plays over exactly [goTime - SWITCH_ANIM_DURATION, goTime] on every
// client. Shared here so the server's schedule and the client's drawing window agree.
var SWITCH_ANIM_DURATION = 1.2;

var WORM_RADIUS = 4;
var WORM_SPEED = 100;
var WORM_TURNING_SPEED = 3.3;

var JUMP_COOLDOWN = 1.5;                // After a jump; this is the minimum waiting time until another jump
var JUMP_CHANCE = 0.5;                    // 0.5 means 50 % chance of jump after 1 second has passed (after the JUMP_COOLDOWN has passed).

var IMMUNITY_DISTANCE_MULTIPLIER = 6;

var STEERING_STRAIGHT = 0;
var STEERING_LEFT = -1;
var STEERING_RIGHT = 1;

var PLAY_AREA_FREE = -1;
var PLAY_AREA_OBSTACLE = -2;

export {
    START_PHASE_DURATION,
    START_PHASE_DURATION_FIRST,
    START_DISTANCE_TO_MAP,
    START_DISTANCE_TO_WORMS,
    wormColors,
    wormColorIds,
    powerUpDefinitions,
    POWER_UP_SPAWN_CHANCE,
    POWER_UP_SHAPE,
    RED_MISSILE_SPEED,
    SWITCH_ANIM_DURATION,

    WORM_RADIUS,
    WORM_SPEED,
    WORM_TURNING_SPEED,

    JUMP_COOLDOWN,
    JUMP_CHANCE,

    IMMUNITY_DISTANCE_MULTIPLIER,

    STEERING_STRAIGHT,
    STEERING_LEFT,
    STEERING_RIGHT,

    PLAY_AREA_FREE,
    PLAY_AREA_OBSTACLE,
}
